import { useMemo } from 'react';
import {
  Globe,
  Smartphone,
  Server,
  CreditCard,
  Search,
  HardDrive,
  Zap,
  ShieldCheck,
  BadgeDollarSign,
  LayoutGrid,
  Handshake,
  MonitorSmartphone,
  ChevronDown,
  MessageSquare,
  Phone,
  ArrowRight,
} from 'lucide-react';

import AnimatedCard from '../components/AnimatedCard';
import SectionHeader from '../components/SectionHeader';
import Seo from '../seo/Seo';
import { SERVICES_SEO } from '../seo/pageSeo';

const SERVICES = [
  {
    Icon: Globe,
    title: 'Website Development',
    desc: 'Modern responsive websites crafted for conversions.',
    color: '#00f5ff',
    tags: ['React', 'Next.js', 'WordPress'],
  },
  {
    Icon: Smartphone,
    title: 'Mobile App Development',
    desc: 'Cross platform mobile apps with premium UI experience.',
    color: '#8b5cf6',
    tags: ['React Native', 'Flutter'],
  },
  {
    Icon: Server,
    title: 'Backend Development',
    desc: 'Scalable APIs and secure backend architecture.',
    color: '#f472b6',
    tags: ['Node.js', 'MongoDB'],
  },
  {
    Icon: CreditCard,
    title: 'Payment Integration',
    desc: 'Razorpay, Stripe & secure payment systems.',
    color: '#00f5ff',
    tags: ['Stripe', 'Razorpay'],
  },
  {
    Icon: Search,
    title: 'SEO Optimization',
    desc: 'Boost rankings and increase organic traffic.',
    color: '#8b5cf6',
    tags: ['SEO', 'Analytics'],
  },
  {
    Icon: HardDrive,
    title: 'Hosting & Domain',
    desc: 'Hosting, SSL and deployment solutions.',
    color: '#f472b6',
    tags: ['Hosting', 'SSL'],
  },
];

const EXTRA_SERVICES = [
  {
    Icon: Zap,
    title: 'Fast Performance',
    desc: 'Lightning fast loading websites and apps.',
    color: '#00f5ff',
  },
  {
    Icon: ShieldCheck,
    title: 'Security',
    desc: 'Secure authentication and data protection.',
    color: '#8b5cf6',
  },
  {
    Icon: BadgeDollarSign,
    title: 'Affordable Pricing',
    desc: 'Premium quality at business friendly pricing.',
    color: '#f472b6',
  },
  {
    Icon: LayoutGrid,
    title: 'UI/UX Design',
    desc: 'Clean and modern user interface systems.',
    color: '#00f5ff',
  },
  {
    Icon: Handshake,
    title: 'Client Support',
    desc: 'Dedicated support and fast communication.',
    color: '#8b5cf6',
  },
  {
    Icon: MonitorSmartphone,
    title: 'Responsive Design',
    desc: 'Perfect layouts across all screen sizes.',
    color: '#f472b6',
  },
];

const DOTS = Array.from({ length: 24 }, (_, i) => ({
  id: i,
  left: Math.random() * 100,
  top: Math.random() * 100,
  size: Math.random() * 5 + 2,
  color: ['#00f5ff', '#8b5cf6', '#f472b6'][i % 3],
  dur: (Math.random() * 3 + 2).toFixed(1),
  delay: (Math.random() * 4).toFixed(1),
}));

function shuffleArray(array) {
  return [...array].sort(() => Math.random() - 0.5);
}

function InfiniteScroll({ items, speed = "40s" }) {

  const duplicated = [...items, ...items];

  return (
    <div className="relative overflow-hidden w-full">

      <div className="absolute left-0 top-0 z-20 h-full w-32 bg-gradient-to-r from-[#050810] to-transparent pointer-events-none" />

      <div className="absolute right-0 top-0 z-20 h-full w-32 bg-gradient-to-l from-[#050810] to-transparent pointer-events-none" />

      <div
        className="flex gap-6 w-max"
        style={{
          animation:`infiniteScroll ${speed} linear infinite`
        }}
      >

        {duplicated.map((s, i) => (
          <article
            key={i}
            className="group relative min-w-[340px] md:min-w-[400px]
            h-[420px] rounded-3xl overflow-hidden
            border border-white/10 bg-[#0d1428]
            transition-all duration-500
            hover:-translate-y-3
            hover:shadow-[0_25px_60px_rgba(0,0,0,.65),0_0_35px_rgba(0,245,255,.18)]"
          >

            {/* glow */}
            <div
              className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500"
              style={{
                background:`radial-gradient(circle,${s.color}25,transparent 70%)`,
                transform:'translate(30%,-30%)'
              }}
            />

            {/* number */}
            <div
              className="absolute bottom-4 right-5 text-[5rem] font-bold pointer-events-none"
              style={{
                color:`${s.color}10`
              }}
            >
              {String(i + 1).padStart(2, '0')}
            </div>

            <div className="p-8 h-full flex flex-col">

              {/* icon */}
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 group-hover:scale-110"
                style={{
                  background:`linear-gradient(135deg,${s.color}20,${s.color}08)`,
                  border:`1px solid ${s.color}35`,
                }}
              >
                <s.Icon size={28} color={s.color} />
              </div>

              <h2 className="text-white text-2xl font-bold mb-4">
                {s.title}
              </h2>

              <p className="text-slate-400 leading-relaxed mb-6">
                {s.desc}
              </p>

              {s.tags && (
                <div className="flex flex-wrap gap-2 mb-8">
                  {s.tags.map(tag => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs"
                      style={{
                        background:`${s.color}10`,
                        border:`1px solid ${s.color}30`,
                        color:s.color
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <button
                className="mt-auto flex items-center gap-2"
                style={{ color:s.color }}
              >
                Learn More
                <ArrowRight size={16} />
              </button>

            </div>

          </article>
        ))}

      </div>
    </div>
  );
}

export default function ServicesPage() {

  const randomServices = useMemo(() => shuffleArray(SERVICES), []);
  const randomExtraServices = useMemo(() => shuffleArray(EXTRA_SERVICES), []);

  return (
    <>
      <Seo {...SERVICES_SEO} />
      {/* HERO */}
      <section
        className="relative w-full overflow-hidden flex items-center"
        style={{ minHeight:'90vh' }}
      >

        {/* background */}
        <div className="absolute inset-0">

          <div className="absolute inset-0 bg-[#050810]" />

          <div
            className="absolute -left-60 top-1/3 w-[700px] h-[700px] rounded-full"
            style={{
              background:'radial-gradient(circle,rgba(0,245,255,.13) 0%,transparent 70%)',
              filter:'blur(50px)'
            }}
          />

          <div
            className="absolute -right-60 top-1/2 w-[600px] h-[600px] rounded-full"
            style={{
              background:'radial-gradient(circle,rgba(139,92,246,.15) 0%,transparent 70%)',
              filter:'blur(50px)'
            }}
          />

          <div
            className="absolute left-1/2 -translate-x-1/2 bottom-0 w-[900px] h-[350px]"
            style={{
              background:'radial-gradient(ellipse,rgba(244,114,182,.08) 0%,transparent 70%)',
              filter:'blur(40px)'
            }}
          />

          <div
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage:'linear-gradient(rgba(0,245,255,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(0,245,255,.03) 1px,transparent 1px)',
              backgroundSize:'50px 50px'
            }}
          />

          {DOTS.map(d => (
            <span
              key={d.id}
              className="absolute rounded-full"
              style={{
                left:`${d.left}%`,
                top:`${d.top}%`,
                width:`${d.size}px`,
                height:`${d.size}px`,
                background:d.color,
                boxShadow:`0 0 ${d.size * 4}px ${d.color}`,
                animation:`dotPulse ${d.dur}s ease-in-out ${d.delay}s infinite alternate`,
                opacity:.55
              }}
            />
          ))}

        </div>

        {/* content */}
        <div className="relative z-10 w-full max-w-6xl mx-auto px-8 py-20 flex flex-col items-center text-center">

          <div className="inline-flex items-center gap-2 px-5 py-2 mb-8 rounded-full border border-cyan-400/30 bg-cyan-400/10 text-cyan-400 text-xs tracking-widest">
            <Server size={13} />
            PREMIUM DIGITAL SERVICES
          </div>

          <h1
            className="text-white font-bold leading-tight mb-6"
            style={{ fontSize:'clamp(2.5rem,6vw,5rem)' }}
          >
            Everything Your <br />
            <span className="grad-text">
              Business Needs
            </span>
          </h1>

          <p className="text-slate-400 max-w-2xl leading-relaxed mb-12">
            We craft premium websites, applications and digital systems
            designed for growth and performance.
          </p>

          <div className="flex flex-wrap justify-center gap-3 mb-14">
            {[
              'Fast Delivery',
              'Premium Design',
              'SEO Ready',
              'Mobile Friendly',
              'Secure Systems',
            ].map((item, i) => (
              <span
                key={i}
                className="px-4 py-2 rounded-full border text-sm"
                style={{
                  borderColor:['rgba(0,245,255,.25)','rgba(139,92,246,.25)','rgba(244,114,182,.25)'][i % 3],
                  background:['rgba(0,245,255,.06)','rgba(139,92,246,.06)','rgba(244,114,182,.06)'][i % 3],
                  color:['#00f5ff','#8b5cf6','#f472b6'][i % 3],
                }}
              >
                {item}
              </span>
            ))}
          </div>

          <ChevronDown
            size={20}
            color="#00f5ff"
            style={{
              animation:'bounce 1.5s infinite'
            }}
          />

        </div>

      </section>

      {/* SERVICES */}
      <section className="py-20 bg-[#050810] overflow-hidden">

        <div className="max-w-7xl mx-auto px-8 mb-12">
          <SectionHeader
            label="OUR SERVICES"
            title="What We Offer"
            sub="Modern digital solutions for modern businesses"
          />
        </div>

        <InfiniteScroll
          items={randomServices}
          speed="35s"
        />

      </section>

      {/* EXTRA */}
      <section className="pb-24 bg-[#050810] overflow-hidden">

        <div className="max-w-7xl mx-auto px-8 mb-12">
          <SectionHeader
            label="WHY CHOOSE US"
            title="Extra Benefits"
            sub="More than development — complete business solutions"
          />
        </div>

        <InfiniteScroll
          items={randomExtraServices}
          speed="45s"
        />

      </section>

      {/* CTA */}
      <section className="px-8 pb-24 bg-[#050810]">

        <div className="max-w-5xl mx-auto">

          <AnimatedCard
            className="p-10 rounded-3xl border border-cyan-400/10 bg-cyan-400/5 text-center"
          >

            <h2 className="text-white text-4xl font-bold mb-4">
              Ready To Build Something Amazing?
            </h2>

            <p className="text-slate-400 mb-8">
              Let’s create a premium digital experience for your business.
            </p>

            <div className="flex flex-wrap justify-center gap-4">

              <a
                href="https://wa.me/919499130923"
                target="#"
                rel="noopener noreferrer"
                className="btn-primary no-underline"
              >
                <MessageSquare size={16} />
                Let’s Talk
              </a>

              <a
                href="tel:+919499130923"
                className="btn-secondary no-underline"
              >
                <Phone size={16} />
                +91 9499130923
              </a>

            </div>

          </AnimatedCard>

        </div>

      </section>

      <style>{`

        @keyframes dotPulse {
          from {
            opacity:.25;
            transform:scale(1);
          }
          to {
            opacity:.75;
            transform:scale(1.7);
          }
        }

        @keyframes infiniteScroll {
          from {
            transform:translateX(0);
          }
          to {
            transform:translateX(calc(-50% - 12px));
          }
        }

        @keyframes bounce {
          0%,100% {
            transform:translateY(0);
          }
          50% {
            transform:translateY(10px);
          }
        }

      `}</style>
    </>
  );
}